import {useCallback, useMemo, useState} from 'react'
import {View} from 'react-native'
import {setStringAsync} from 'expo-clipboard'
import {msg} from '@lingui/core/macro'
import {useLingui} from '@lingui/react'
import {Trans} from '@lingui/react/macro'
import {useNavigation} from '@react-navigation/native'
import {type NativeStackScreenProps} from '@react-navigation/native-stack'

import {useDemoFamily} from '#/lib/demoFamily'
import {
  type CommonNavigatorParams,
  type NavigationProp,
} from '#/lib/routes/types'
import {
  makeFamilyInviteLink,
  useConfirmFamilyLink,
  useDeclineFamilyLink,
  useFamilyLinkPreviewQuery,
} from '#/state/queries/family'
import {useSession} from '#/state/session'
import {useLoggedOutViewControls} from '#/state/shell/logged-out'
import {FamilyDeclineDialog} from '#/screens/Settings/components/FamilyDeclineDialog'
import {FamilyInviteCard} from '#/screens/Settings/components/FamilyInviteCard'
import {atoms as a, useTheme} from '#/alf'
import {Button, ButtonIcon, ButtonText} from '#/components/Button'
import * as Dialog from '#/components/Dialog'
import {ChainLink_Stroke2_Corner0_Rounded as ChainLinkIcon} from '#/components/icons/ChainLink'
import * as Layout from '#/components/Layout'
import {ListMaybePlaceholder} from '#/components/Lists'
import {Loader} from '#/components/Loader'
import * as Toast from '#/components/Toast'
import {Text} from '#/components/Typography'

type Props = NativeStackScreenProps<CommonNavigatorParams, 'FamilyInvite'>

export function FamilyInviteScreen({route}: Props) {
  const {_} = useLingui()
  const t = useTheme()
  const {hasSession} = useSession()
  const navigation = useNavigation<NavigationProp>()
  const {requestSwitchToAccount} = useLoggedOutViewControls()
  const demo = useDemoFamily()

  const code = route.params.code
  const {data: preview, isLoading, isError, error} =
    useFamilyLinkPreviewQuery(code)
  const confirm = useConfirmFamilyLink()
  const decline = useDeclineFamilyLink()
  const [acceptError, setAcceptError] = useState<string | null>(null)
  const [declined, setDeclined] = useState(false)
  const declineDialogControl = Dialog.useDialogControl()

  const link = makeFamilyInviteLink(code)
  // Memoized so the expiry check doesn't churn on every render. Date.now is
  // deliberately impure (the invite TTL is wall-clock by design).
  const isExpired = useMemo(
    () =>
      preview?.expiresAt != null &&
      new Date(preview.expiresAt).getTime() <=
        // eslint-disable-next-line react-hooks/purity
        Date.now(),
    [preview],
  )

  const onCopyLink = useCallback(async () => {
    await setStringAsync(link)
    Toast.show(_(msg`Copied to clipboard`), {type: 'success'})
  }, [link, _])

  const onAccept = useCallback(() => {
    setAcceptError(null)
    confirm.mutate(code, {
      onSuccess: () => {
        Toast.show(_(msg`Family linked on Taderok! 🎉`), {type: 'success'})
        navigation.navigate(demo.enabled ? 'FamilySettings' : 'Hub')
      },
      onError: e => {
        const err = (e as {error?: string}).error
        if (err === 'LinkRequestNotFound' || err === 'LinkRequestExpired') {
          setAcceptError(_(msg`This invitation is invalid or has expired.`))
        } else if (err === 'ParentLimitReached') {
          setAcceptError(_(msg`This learner already has 2 linked parents.`))
        } else if (err === 'ChildLimitReached') {
          setAcceptError(_(msg`This parent already has 5 linked children.`))
        } else {
          setAcceptError(_(msg`Could not accept this invitation.`))
        }
      },
    })
  }, [confirm, code, navigation, _, demo.enabled])

  const onDecline = useCallback(
    (reason: string, note?: string) => {
      decline.mutate(
        {code, reason, note},
        {
          onSuccess: () => {
            setDeclined(true)
            Toast.show(_(msg`Invitation declined — the sender has been notified.`), {
              type: 'success',
            })
          },
          onError: e => {
            const err = e as {error?: string; message?: string}
            const notImplemented =
              err.error === 'MethodNotImplemented' ||
              /not implemented/i.test(err.message ?? '')
            Toast.show(
              notImplemented
                ? _(msg`Declining invitations isn't available on this server yet.`)
                : _(msg`Could not decline this invitation.`),
              {type: 'error'},
            )
          },
        },
      )
    },
    [decline, code, _],
  )

  if (isLoading) {
    return (
      <Layout.Screen>
        <ListMaybePlaceholder isLoading={true} isError={false} />
      </Layout.Screen>
    )
  }

  if (isError || !preview) {
    const err = error as {error?: string; message?: string} | undefined
    const notImplemented =
      err?.error === 'MethodNotImplemented' ||
      /not implemented/i.test(err?.message ?? '')
    return (
      <Layout.Screen>
        <Layout.Content centerContent>
          <View style={[a.py_4xl, a.px_xl, a.align_center, a.gap_lg]}>
            <Text style={[a.text_2xl, a.font_bold]}>
              {notImplemented ? (
                <Trans>Family linking isn't available yet</Trans>
              ) : (
                <Trans>Invitation unavailable</Trans>
              )}
            </Text>
            <Text style={[a.text_md, a.text_center]}>
              {notImplemented ? (
                <Trans>
                  Family linking isn't available on this server yet. Check back
                  soon.
                </Trans>
              ) : (
                <Trans>
                  This invitation link is invalid or has expired. Ask for a new
                  code from the Family screen.
                </Trans>
              )}
            </Text>
          </View>
        </Layout.Content>
      </Layout.Screen>
    )
  }

  const initiator = {
    did: preview.initiatorDid,
    handle: preview.initiator.handle,
    displayName: preview.initiator.displayName,
    avatar: preview.initiator.avatar,
  }

  return (
    <Layout.Screen>
      <Layout.Content centerContent>
        <View
          style={[
            a.py_4xl,
            a.px_lg,
            a.align_center,
            a.gap_lg,
            {maxWidth: 480},
          ]}>
          <FamilyInviteCard profile={initiator} role={preview.initiatorRole} />
          {isExpired ? (
            <View style={[a.w_full, a.gap_sm]}>
              <Text
                style={[
                  {color: t.palette.negative_400},
                  a.text_md,
                  a.text_center,
                ]}>
                <Trans>
                  This invitation has expired. Ask for a new code from the
                  Family screen.
                </Trans>
              </Text>
            </View>
          ) : demo.enabled && !demo.familyEnabled && hasSession ? (
            <View style={[a.w_full, a.gap_sm]}>
              <Text
                style={[
                  a.text_md,
                  a.text_center,
                  t.atoms.text_contrast_medium,
                ]}>
                <Trans>
                  You've turned off family invitations. Turn them back on in
                  Settings › Account › Family to accept this invitation.
                </Trans>
              </Text>
              <Button
                label={_(msg`Open Family settings`)}
                size="small"
                color="secondary"
                onPress={() => navigation.navigate('FamilySettings')}>
                <ButtonText>
                  <Trans>Open Family settings</Trans>
                </ButtonText>
              </Button>
            </View>
          ) : hasSession ? (
            declined ? (
              <View style={[a.w_full, a.gap_sm]}>
                <Text
                  style={[a.text_md, a.text_center, a.leading_snug]}>
                  <Trans>
                    You declined this invitation. The sender received a kind
                    message letting them know.
                  </Trans>
                </Text>
                <Button
                  label={_(msg`Done`)}
                  size="large"
                  color="secondary"
                  onPress={() =>
                    navigation.navigate(
                      demo.enabled ? 'FamilySettings' : 'Home',
                    )
                  }>
                  <ButtonText>
                    <Trans>Done</Trans>
                  </ButtonText>
                </Button>
              </View>
            ) : (
              <View style={[a.w_full, a.gap_sm]}>
                <Button
                  label={_(msg`Accept invitation`)}
                  size="large"
                  color="primary"
                  disabled={confirm.isPending}
                  onPress={onAccept}>
                  <ButtonText>
                    {confirm.isPending ? (
                      <Trans>Accepting…</Trans>
                    ) : (
                      <Trans>Accept invitation</Trans>
                    )}
                  </ButtonText>
                  {confirm.isPending && <Loader size="xs" />}
                </Button>
                {acceptError ? (
                  <Text
                    style={[
                      {color: t.palette.negative_400},
                      a.text_sm,
                      a.text_center,
                    ]}>
                    {acceptError}
                  </Text>
                ) : null}
                <Button
                  label={_(msg`Decline invitation`)}
                  size="large"
                  color="secondary"
                  disabled={decline.isPending}
                  onPress={() => declineDialogControl.open()}>
                  <ButtonText>
                    <Trans>Decline</Trans>
                  </ButtonText>
                </Button>
                <Button
                  label={_(msg`Copy invitation link`)}
                  size="small"
                  color="secondary"
                  onPress={onCopyLink}>
                  <ButtonIcon icon={ChainLinkIcon} />
                  <ButtonText>
                    <Trans>Copy invitation link</Trans>
                  </ButtonText>
                </Button>
              </View>
            )
          ) : (
            <View style={[a.w_full, a.gap_sm]}>
              <Button
                label={_(msg`Sign in to accept`)}
                size="large"
                color="primary"
                onPress={() => requestSwitchToAccount({})}>
                <ButtonText>
                  <Trans>Sign in to accept</Trans>
                </ButtonText>
              </Button>
              <Button
                label={_(msg`Copy invitation link`)}
                size="small"
                color="secondary"
                onPress={onCopyLink}>
                <ButtonIcon icon={ChainLinkIcon} />
                <ButtonText>
                  <Trans>Copy invitation link</Trans>
                </ButtonText>
              </Button>
            </View>
          )}
        </View>
      </Layout.Content>
      <FamilyDeclineDialog
        control={declineDialogControl}
        peerName={preview.initiator.displayName}
        isPending={decline.isPending}
        onConfirm={onDecline}
      />
    </Layout.Screen>
  )
}
